import React, { useState, useEffect, useRef } from 'react';
import { ALargeSmall, Check } from 'lucide-react';

const FONT_SIZES = [
  { key: 'small', label: 'Small', rootPx: '14px', sample: 'text-xs' },
  { key: 'medium', label: 'Medium (Default)', rootPx: '', sample: 'text-sm' },
  { key: 'large', label: 'Large', rootPx: '18px', sample: 'text-base' },
  { key: 'xlarge', label: 'Extra Large', rootPx: '20px', sample: 'text-lg' }
];

const STORAGE_KEY = 'mvm_font_size';

// Applies the chosen root font size. Tailwind text classes are rem-based,
// so this scales all standard text app-wide.
export const applyFontSize = (key) => {
  const found = FONT_SIZES.find(f => f.key === key) || FONT_SIZES[1];
  document.documentElement.style.fontSize = found.rootPx;
};

const FontSizeControl = ({ className = '' }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => localStorage.getItem(STORAGE_KEY) || 'medium');
  const ref = useRef(null);

  useEffect(() => {
    applyFontSize(current);
  }, [current]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (key) => {
    setCurrent(key);
    localStorage.setItem(STORAGE_KEY, key);
    applyFontSize(key);
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-300 dark:hover:bg-gray-700"
        title="Change text size"
        data-testid="font-size-toggle-btn"
      >
        <ALargeSmall className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-2 w-48 bg-white dark:bg-card rounded-lg shadow-2xl border border-gray-200 dark:border-border overflow-hidden z-[99999]"
          data-testid="font-size-menu"
        >
          <div className="px-3 py-2 border-b border-gray-100 dark:border-border">
            <p className="text-xs font-semibold text-gray-500 dark:text-muted-foreground">Text Size</p>
          </div>
          {FONT_SIZES.map((f) => (
            <button
              key={f.key}
              onClick={() => select(f.key)}
              data-testid={`font-size-option-${f.key}`}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-secondary transition-colors ${current === f.key ? 'bg-teal-50 dark:bg-teal-900/20' : ''}`}
            >
              <span className={`${f.sample} font-medium text-gray-800 dark:text-foreground`}>{f.label}</span>
              {current === f.key && <Check className="w-4 h-4 text-teal-600 dark:text-teal-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FontSizeControl;
